// ============================================
// Review sources
// ============================================
//
// One loaded export, in the shape the review workspace wants: the unit's name,
// its impacts, its verdicts, and everything needed to write the verdicts back
// into the file they came from.
//
// This lives apart from both screens that use it because they used to build it
// twice, and the copies drifted: adding a second unit meant leaving the video
// behind and re-picking it on a different surface. A source is a source
// wherever it was opened from.

import { t } from "i18n";
import { parseExportFile, buildExportFile, exportFileName } from "./exportFile";
import {
  buildExportResult,
  DEFAULT_IMPACT_THRESHOLD_G,
} from "./wearableExport";
import { wearableShortName } from "./centrifugeFile";
import { loadReview, mergeReview, saveReview } from "./videoReview";
import { saveTextFile } from "./fileDialog";

// Distinct at a glance, and distinguishable with the common colour-vision
// deficiencies: no red/green pair adjacent in the order.
export const UNIT_COLORS = [
  "#2563EB",
  "#E07A3A",
  "#0D9488",
  "#9333EA",
  "#B45309",
  "#DB2777",
  "#0EA5E9",
  "#4B5563",
  "#16A34A",
  "#DC2626",
];

/** Only the scalars the review reads. Dropping the sample arrays matters: a
 *  full store is millions of samples per unit, and the review never plots
 *  them -- it reads a peak, a duration and a gate verdict per event. */
function slimRecord(r) {
  return {
    sessionId: r.sessionId,
    impactId: r.impactId,
    frameIndex: r.frameIndex,
    timestampMs: r.timestampMs,
    timestampIsEpoch: r.timestampIsEpoch,
    sessionStartMs: r.sessionStartMs,
    peakMagG: r.peakMagG,
    durationMs: r.durationMs,
    overCount: r.overCount,
    band: r.band,
    isSnippet: r.isSnippet,
    sizeLabel: r.sizeLabel,
    gateFlagsLabel: r.gateFlagsLabel,
    gateFlags: r.gateFlags,
  };
}

/**
 * Read one saved export into a review source.
 *
 * `handle` only has to answer two questions: what are you called, and what do
 * you say. A picked `File` answers both already; anything else can be passed
 * as `{ name, text: async () => "..." }`. The handle is kept so saving can
 * re-read from disk instead of holding tens of megabytes of base64 per unit.
 */
export async function loadReviewSource(handle, { id, colorIndex = 0 } = {}) {
  const text = await handle.text();
  const file = parseExportFile(text);
  const decoded = buildExportResult(file.raw, {
    impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
  });
  const records = decoded.records.filter(
    (r) => r && !r.isMarker && r.isValid !== false
  );
  if (records.length === 0) throw new Error(t("utils.reviewSources.noImpacts"));

  const mac = file.wearable?.ble_mac || decoded.mac || handle.name;
  // A file may already carry verdicts (export v3+). Anything decided on this
  // machine still wins, so opening a file cannot undo work in progress.
  const review = mergeReview(file.review, loadReview(mac));

  return {
    id: id || `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    name: wearableShortName(file.wearable, mac),
    color: UNIT_COLORS[colorIndex % UNIT_COLORS.length],
    fileName: handle.name,
    handle,
    mac,
    meta: {
      wearable: file.wearable,
      status: file.status,
      clock: file.clock,
      exportedAt: file.exportedAt,
    },
    decoded,
    records: records.map(slimRecord),
    review,
    offsetMs: review.offsetMs || 0,
  };
}

/** Write a source's file back out, now carrying its verdicts. The raw bytes
 *  are re-read from disk, so the saved copy is the original export with only
 *  the review and the decoded digest rebuilt. */
export async function saveReviewSource(source) {
  const file = parseExportFile(await source.handle.text());
  const decoded = buildExportResult(file.raw, {
    impactThresholdG: DEFAULT_IMPACT_THRESHOLD_G,
  });
  const { json } = buildExportFile({
    wearable: file.wearable,
    mac: source.mac,
    raw: file.raw,
    // The export happened when it happened. Tagging it later must not rewrite
    // when the data came off the device.
    exportedAt: file.exportedAt,
    status: file.status,
    clock: file.clock,
    parsed: decoded,
    review: source.review,
  });
  return saveTextFile({
    defaultName:
      source.fileName ||
      exportFileName({ wearable: file.wearable, mac: source.mac }),
    contents: json,
  });
}

/** Persist a source's review to this browser and hand back the updated source.
 *  Kept here so every screen writes to the same key with the same shape. */
export function withReview(source, fn) {
  const review = typeof fn === "function" ? fn(source.review) : fn;
  saveReview(source.mac, review);
  return { ...source, review, offsetMs: review.offsetMs || source.offsetMs };
}
